import bs58 from 'bs58';
import { Transaction, VersionedMessage, VersionedTransaction } from '@solana/web3.js';
import { BASE58_CHARS, BASE58_REGEX, SPL_SUPPLY_U64_MAX, VANITY_MAX_TOTAL_CHARS } from '../constants';

export function decodeSerializedTransactionBytes(raw) {
  const s = typeof raw === 'string' ? raw.trim() : '';
  if (!s) throw new Error('Empty serializedTransaction');
  const looksBase64 =
    s.includes('+') || s.includes('/') || /=$/.test(s) || (s.length % 4 === 0 && /^[A-Za-z0-9+/]+=*$/.test(s));
  if (looksBase64) {
    try {
      const buf = Buffer.from(s, 'base64');
      if (buf.length > 0) return buf;
    } catch {
    }
  }
  try {
    return Buffer.from(bs58.decode(s));
  } catch {
    return Buffer.from(s, 'base64');
  }
}

export function deserializeMintTransactionFromBackend(txBuffer) {
  const bytes = txBuffer instanceof Uint8Array ? txBuffer : new Uint8Array(txBuffer);
  try {
    return VersionedTransaction.deserialize(bytes);
  } catch {
    try {
      return new VersionedTransaction(VersionedMessage.deserialize(bytes));
    } catch {
      return Transaction.from(txBuffer);
    }
  }
}

export function isVersionedTransaction(txBuffer) {
  if (!txBuffer || txBuffer.length === 0) return false;
  return (txBuffer[0] & 0x80) !== 0;
}

export function getMaxSupplyForDecimals(decimals) {
  const d = Number(decimals);
  if (isNaN(d) || d < 0 || d > 9) return SPL_SUPPLY_U64_MAX;
  return SPL_SUPPLY_U64_MAX / (BigInt(10) ** BigInt(d));
}

export function numberToEnglish(numStr) {
  const n = numStr.replace(/\D/g, '') || '0';
  if (n === '0') return 'zero';
  const num = BigInt(n);
  if (num < 1000) return num.toString();
  if (num < 1_000_000) return Number(n).toLocaleString();

  const b = 1_000_000_000n;
  const m = 1_000_000n;
  const k = 1_000n;

  const formatWithUnit = (value, remainder, divisor, unit) => {
    if (remainder === 0n) return `${value} ${unit}`;
    const frac = Number(remainder) / Number(divisor);
    const decimals = frac < 0.01 ? 0 : frac < 0.1 ? 2 : 1;
    const combined = Number(value) + frac;
    const rounded = decimals === 0 ? combined : Number(combined.toFixed(decimals));
    if (rounded === Math.floor(rounded)) return `${rounded} ${unit}`;
    return `${rounded.toFixed(decimals).replace(/\.?0+$/, '')} ${unit}`;
  };

  if (num >= b) return formatWithUnit(num / b, num % b, b, 'billion');
  if (num >= m) return formatWithUnit(num / m, num % m, m, 'million');
  if (num >= k) {
    const thousands = num / k;
    if (num % k === 0n) return `${thousands} thousand`;
    return Number(num).toLocaleString();
  }
  return num.toString();
}

export function countValidSocialLinks(form) {
  let n = 0;
  if (form.website?.trim().startsWith('https://')) n++;
  if (form.telegram?.trim().startsWith('https://t.me/')) n++;
  if (/^https:\/\/(twitter\.com|x\.com)\/.+/.test(form.twitter?.trim() || '')) n++;
  if (/^https:\/\/(discord\.com|discord\.gg)\/.+/.test(form.discord?.trim() || '')) n++;
  if (/^https:\/\/(www\.)?github\.com\/.+/.test(form.github?.trim() || '')) n++;
  if (/^https:\/\/(www\.)?reddit\.com\/.+/.test(form.reddit?.trim() || '')) n++;
  if (/^https:\/\/(medium\.com|.+\.medium\.com)\/.+/.test(form.medium?.trim() || '')) n++;
  if (/^https:\/\/(www\.)?instagram\.com\/.+/.test(form.instagram?.trim() || '')) n++;
  if (/^https:\/\/(www\.)?(youtube\.com|youtu\.be)\/.+/.test(form.youtube?.trim() || '')) n++;
  if (/^https:\/\/(www\.)?linkedin\.com\/.+/.test(form.linkedin?.trim() || '')) n++;
  return n;
}

export function sanitizeBase58(str = '') {
  return str
    .replace(/\s+/g, '')
    .split('')
    .filter((ch) => BASE58_CHARS.includes(ch))
    .join('');
}

export function validateVanity(prefix, suffix) {
  const hasPrefix = prefix?.trim().length > 0;
  const hasSuffix = suffix?.trim().length > 0;
  if (hasPrefix && hasSuffix) throw new Error('Use only prefix or suffix, not both.');
  if (prefix && !BASE58_REGEX.test(prefix)) throw new Error('Prefix contains non-base58 characters');
  if (suffix && !BASE58_REGEX.test(suffix)) throw new Error('Suffix contains non-base58 characters');
  const total = (prefix?.length || 0) + (suffix?.length || 0);
  if (total > VANITY_MAX_TOTAL_CHARS) {
    throw new Error(`Maximum is ${VANITY_MAX_TOTAL_CHARS} characters. You have ${total}.`);
  }
}

export function isValidSolanaWalletAddress(addr) {
  if (!addr || typeof addr !== 'string') return false;
  const trimmed = addr.trim();
  if (trimmed.length < 32 || trimmed.length > 44) return false;
  return /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(trimmed);
}
